import * as React from 'react';
import * as Cookies from 'js-cookie';
import { withRouter, RouteProps } from 'react-router';
import { EDIT_NEW_PROJECT, NEW_PROJECT } from '../../../constants/routes';
import NotifyHOC from '../../../HOC/Notifications';
import { packagedSolution, rapidSolutionOrder } from '../../../mapping/project';
import { getPackagedSolution } from '../../../services/project.service';
import {
    getTenantLevel,
    getUserDetails
} from '@nexxt/common/firebase/auth/userDetails';
import { interpolate } from '../../../utils/string';
import Spinner from '../../common/Loader/Spinner';
import {
    PACKAGED_SOLUTION_QIDS,
    STATIC_STORAGE_URL
} from '@nexxt/common/constants';
import { TENANT_LEVEL } from '@nexxt/common/types';
import config from '../../../config';
import axios from 'axios';
import { INITIAL_SESSION, PortalSession } from '../../../constants/project';

interface ProjectSelectionProps {
    toastNotification: any;
}

const ProjectSelection: React.FC = (
    props: RouteProps & ProjectSelectionProps
) => {
    const tenantLevel = getTenantLevel();
    const user = getUserDetails();
    const [isLoading, setIsLoading] = React.useState(false);
    const [session, setSession] =
        React.useState<PortalSession>(INITIAL_SESSION);
    const filteredPackagedSolution = packagedSolution.filter((ps) =>
        ps?.allowedTenantLevels.includes(tenantLevel)
    );
    const isWizard = tenantLevel === TENANT_LEVEL.INCA_WIZARD;
    const [eligibleTenantIDs, setEligibleTenantIDs] = React.useState([
        TENANT_LEVEL.GLOBAL
    ]);

    React.useEffect(() => {
        if (user.allowDevMode) {
            setEligibleTenantIDs((prevState) => [
                ...prevState,
                TENANT_LEVEL.PENDING
            ]);
        }
        loadSession();
    }, []);

    const loadSession = async (): Promise<void> => {
        setIsLoading(true);
        try {
            const DATA_API_URL = `${config.host.data}/session/load/portal/${user.id}`;
            const cssToken = Cookies.get('csst');
            const signal = axios.CancelToken.source();
            const res = await axios.get(DATA_API_URL, {
                cancelToken: signal.token,
                headers: { Authorization: `Bearer ${cssToken}` },
                withCredentials: true
            });

            // apply session if it exists
            if (res.status === 200) {
                const loadedSession = {
                    ...INITIAL_SESSION,
                    ...res.data
                };
                setSession(loadedSession);
            }
        } catch (error) {
            // if package session doesn't exist, use template session
            setSession({
                ...INITIAL_SESSION
            });
        }
        setIsLoading(false);
    };

    const saveSession = async (newSession): Promise<void> => {
        const DATA_API_URL = `${config.host.data}/session/save`;
        const signal = axios.CancelToken.source();
        const body = {
            sessionKey: user.id,
            sessionContext: 'portal',
            sessionObject: newSession
        };

        try {
            const cssToken = Cookies.get('csst');
            await axios.post(DATA_API_URL, body, {
                cancelToken: signal.token,
                headers: { Authorization: `Bearer ${cssToken}` },
                withCredentials: true
            });
            setSession(newSession);
        } catch (error) {
            console.log('ERROR! Could not save session:', error);
        }
    };

    const updateSeen = async (solution) => {
        try {
            await saveSession({
                ...session,
                seenRapidSolutions: {
                    ...session.seenRapidSolutions,
                    [solution.rapidSolutionID]: true
                }
            });
        } catch (err) {
            console.log(
                'ERROR! Could not update seenRapidSolution Session:',
                err
            );
        }
    };

    const redirectToProject = async (solution) => {
        setIsLoading(true);
        if (solution.to) {
            props.history.push(solution.to);
        } else if (isWizard) {
            await updateSeen(solution);
            props.history.push({
                pathname: NEW_PROJECT,
                state: { solution }
            });
        } else {
            try {
                await getPackagedSolution(
                    solution.projectID,
                    solution.tenantID,
                    solution.title
                ).then((res) => {
                    setIsLoading(false);
                    props.history.push({
                        pathname: `${interpolate(EDIT_NEW_PROJECT, {
                            projectId: res.data.data
                        })}`
                    });
                });
            } catch (e) {
                setIsLoading(false);
                props.toastNotification.error(e.response.data.message);
            }
        }
    };

    return isLoading ? (
        <Spinner />
    ) : (
        <div className="project-selection-content">
            {isWizard && (
                <div className="project-selection-container">
                    <div className="project-selection--card-container--expanded">
                        {filteredPackagedSolution
                            .filter(
                                (solution) =>
                                    eligibleTenantIDs.includes(
                                        Number(solution.tenantID)
                                    ) &&
                                    !solution.disabledTenantLevels.includes(
                                        tenantLevel
                                    )
                            )
                            .reverse()
                            .sort((a, b) => {
                                return a.rapidSolutionID ==
                                    session.featuredRapidSolution
                                    ? -1
                                    : b.rapidSolutionID ==
                                      session.featuredRapidSolution
                                    ? 1
                                    : 0;
                            })
                            .map((ps, index) => {
                                const newRapidSolution =
                                    ps.rapidSolutionID ===
                                    rapidSolutionOrder[
                                        rapidSolutionOrder.length - 1
                                    ];

                                return (
                                    <SolutionCardExpanded
                                        {...props}
                                        key={index}
                                        solution={ps}
                                        newRapidSolution={newRapidSolution}
                                        disabled={
                                            !ps.projectID ||
                                            ps.disabledTenantLevels.includes(
                                                tenantLevel
                                            )
                                        }
                                        redirectToProject={redirectToProject}
                                        updateSeen={updateSeen}
                                        enableFlag={
                                            ps.rapidSolutionID ===
                                            session.featuredRapidSolution
                                        }
                                    />
                                );
                            })}
                    </div>
                </div>
            )}
            <div className="project-selection-container">
                {isWizard && (
                    <div className="project-selection--title">Coming Soon</div>
                )}
                <div className="project-selection--card-container">
                    {filteredPackagedSolution
                        .filter((solution) => {
                            if (isWizard) {
                                return !(
                                    eligibleTenantIDs.includes(
                                        Number(solution.tenantID)
                                    ) &&
                                    !solution.disabledTenantLevels.includes(
                                        tenantLevel
                                    )
                                );
                            } else {
                                return solution.allowedTenantLevels.includes(
                                    tenantLevel
                                );
                            }
                        })
                        .map((ps, index) => {
                            return (
                                <SolutionCard
                                    {...props}
                                    key={index}
                                    solution={ps}
                                    disabled={
                                        !ps.projectID ||
                                        ps.disabledTenantLevels.includes(
                                            tenantLevel
                                        )
                                    }
                                    redirectToProject={redirectToProject}
                                    enableFlag={
                                        ps.projectID ===
                                        PACKAGED_SOLUTION_QIDS.BULID_FROM_SCRATCH
                                    }
                                />
                            );
                        })}
                </div>
            </div>
        </div>
    );
};

export default NotifyHOC(withRouter(ProjectSelection));

interface ISolutionCardProps {
    solution: any;
    enableFlag: boolean;
    disabled?: boolean;
    history?: any;
    redirectToProject: (solution) => void;
}
const SolutionCard: React.FC<ISolutionCardProps> = (
    props: ISolutionCardProps
) => {
    const { title, subtitle, body } = props.solution;

    return (
        <div className="project-selection--card">
            {props.enableFlag && (
                <div className="project-selection--card--flag"></div>
            )}
            <img
                className="project-selection--card--body--icon"
                src={props.solution.image}
            />
            <div className="project-selection--card--body">
                <div className="project-selection--card--body-wrapper">
                    <p className="project-selection--card--title">{title}</p>
                    <div className="project-selection--card--body--description">
                        {body}
                    </div>
                </div>
                <div className="project-selection--card--buttons">
                    <button
                        className={`btn btn--sm btn--green ${
                            props.disabled ? 'disabled' : ''
                        }`}
                        disabled={props.disabled}
                        data-test={title.replace(/\s+/g, '-').toLowerCase()}
                        onClick={() => props.redirectToProject(props.solution)}
                    >
                        {props.disabled ? 'Coming soon' : 'Select'}
                    </button>
                </div>
            </div>
        </div>
    );
};

interface ISolutionCardExpandedProps {
    solution: any;
    enableFlag: boolean;
    disabled?: boolean;
    history?: any;
    newRapidSolution: boolean;
    redirectToProject: (solution) => void;
    updateSeen: (solution) => void;
}
const SolutionCardExpanded: React.FC<ISolutionCardExpandedProps> = (
    props: ISolutionCardExpandedProps
) => {
    const { title, subtitle, body, codename } = props.solution;

    return (
        <div className="project-selection--card expanded-card">
            {props.enableFlag && (
                <div className="project-selection--card--flag --mini"></div>
            )}
            <div className="project-selection--card-expanded">
                <span className="project-selection--card-expanded--title">
                    {title} <span>{props.newRapidSolution && 'NEW'}</span>
                </span>
                <p className="project-selection--card-expanded--body">{body}</p>
                <div className="project-selection--card-expanded--images">
                    <div className="project-selection--card-expanded--images--item">
                        <img
                            className="project-selection--card-expanded--images--chat"
                            src={`${STATIC_STORAGE_URL}/img/portal/packaged-solutions/${title
                                .toLowerCase()
                                .replaceAll(' ', '-')}-chat.png`}
                        />
                    </div>
                    <div className="project-selection--card-expanded--images--item">
                        <img
                            className="project-selection--card-expanded--images--dashboard"
                            src={`${STATIC_STORAGE_URL}/img/portal/packaged-solutions/${title
                                .toLowerCase()
                                .replaceAll(' ', '-')}-dashboard.png`}
                        />
                    </div>
                </div>
                <div className="project-selection--card-expanded--actions">
                    <div className="project-selection--card-expanded--actions--preview">
                        <div className="project-selection--card-expanded--actions--preview--header">
                            Preview Demo:
                        </div>
                        <div className="project-selection--card-expanded--actions--preview--buttons">
                            <button
                                className={`btn btn--sm btn--blue`}
                                onClick={() => {
                                    props.updateSeen(props.solution);
                                    window.open(
                                        `${config.host.bot}/p/${codename}`,
                                        '_blank'
                                    );
                                }}
                            >
                                Chat
                            </button>

                            <button
                                className={`btn btn--sm btn--blue`}
                                onClick={() => {
                                    props.updateSeen(props.solution);
                                    window.open(
                                        `${config.host.dashboard}/p/${codename}/reports`,
                                        '_blank'
                                    );
                                }}
                            >
                                Dashboard
                            </button>
                        </div>
                    </div>
                    <button
                        className={`btn btn--sm btn--green`}
                        onClick={() => props.redirectToProject(props.solution)}
                    >
                        Select Template
                    </button>
                </div>
            </div>
        </div>
    );
};
